
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle } from 'lucide-react';
import { 
  defaultTimeHorizons, 
  defaultDayHorizons, 
  defaultAlmData,
} from './index';
import ALMTimelineSelector from './ALMTimelineSelector';
import { BorrowingSheet } from './borrowings/BorrowingSheet';
import ALMDashboardHeader from './ALMDashboardHeader';
import useALMCalculations from './hooks/useALMCalculations';
import useTimeHorizonManager from './hooks/useTimeHorizonManager';
import ContractDetailSheet from './contracts/ContractDetailSheet';
import { ALMContract } from './contracts/types';
import ALMPositionTable from './ALMPositionTable';
import { sampleContracts } from './contracts/sampleContractsData';

const ALMDashboard = () => {
  // State for ALM data
  const [almData, setAlmData] = useState(defaultAlmData);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  
  // Sheet management state
  const [borrowSheet, setBorrowSheet] = useState({ open: false, amount: 0, horizon: "" });
  
  // Contract detail sheet state
  const [contractDetailSheet, setContractDetailSheet] = useState({ 
    open: false, 
    contract: null as ALMContract | null 
  });
  
  // Use our custom hooks
  const { 
    granularity, 
    currentHorizons,
    handleGranularityChange,
    handleAddTimeHorizon,
    handleRemoveTimeHorizon 
  } = useTimeHorizonManager({
    initialTimeHorizons: defaultTimeHorizons,
    initialDayHorizons: defaultDayHorizons,
    initialGranularity: "months",
    almData,
    setAlmData
  });
  
  // Ensure data is loaded with a useEffect
  useEffect(() => {
    // Force a data refresh using a timeout
    const timer = setTimeout(() => {
      setIsDataLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  const { calculateTotals } = useALMCalculations(almData, currentHorizons);
  const totals = calculateTotals();
  
  // Handle opening the borrow sheet
  const handleOpenBorrowSheet = (amount: number, horizonLabel: string) => {
    setBorrowSheet({ open: true, amount: Math.abs(amount), horizon: horizonLabel });
  };

  // Handle opening contract details
  const handleContractClick = (contract: ALMContract) => {
    setContractDetailSheet({ open: true, contract });
  };

  return (
    <>
      <ALMDashboardHeader title="Asset Liability Management" />

      <ALMTimelineSelector 
        timeHorizons={currentHorizons}
        onAddTimeHorizon={handleAddTimeHorizon}
        onRemoveTimeHorizon={handleRemoveTimeHorizon}
        granularity={granularity}
        onGranularityChange={handleGranularityChange}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle>ALM Position Analysis</CardTitle>
              <CardDescription>
                Cash flow projections across different time horizons
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <ALMPositionTable 
              almData={almData}
              currentHorizons={currentHorizons}
              totals={totals}
              onOpenBorrowSheet={handleOpenBorrowSheet}
              contracts={sampleContracts}
              onContractClick={handleContractClick}
              key={isDataLoaded ? "loaded" : "loading"}
            />
          </CardContent>
          <CardFooter className="flex justify-between">
            <div className="flex items-center">
              <AlertTriangle className="h-4 w-4 text-amber-500 mr-2" />
              <span className="text-sm text-muted-foreground">
                {totals.netPosition[currentHorizons[1]?.id] < 0 ? 
                  `Cash deficit detected in ${currentHorizons[1]?.label} horizon` : 
                  "No cash deficit detected in near-term horizons"}
              </span>
            </div>
          </CardFooter>
        </Card>
      </div>
      
      {/* Add the borrow sheet */}
      <BorrowingSheet 
        open={borrowSheet.open} 
        onOpenChange={(open) => setBorrowSheet({ ...borrowSheet, open })} 
        amount={borrowSheet.amount} 
        timeHorizon={borrowSheet.horizon} 
      />

      {/* Add the contract detail sheet */}
      <ContractDetailSheet
        open={contractDetailSheet.open}
        onOpenChange={(open) => setContractDetailSheet({ ...contractDetailSheet, open })}
        contract={contractDetailSheet.contract}
      />
    </>
  );
};

export default ALMDashboard;
